/**
 * Suspensiones POR CASA (Sprint 19a — Suspensiones-rediseño, Opción B).
 *
 * Reescrito de "selector de casa + accesos individuales" (Sprint 18) a una
 * LISTA GLOBAL por casa con dos tabs:
 *   1. Suspendibles — casas habilitadas para suspender (convención #20,
 *      2026-10-06), nivel calculado EN VIVO (no del snapshot diario) +
 *      buscador de cualquier casa.
 *   2. Suspendidas / en proceso — máquina de estados de la casa.
 *
 * El estado de suspensión vive en la CASA, no por acceso: los accesos heredan
 * el estado de la casa. Qué se opera (2026-10-07): el WIFI lo corta y lo
 * restaura el sistema en eWeLink al confirmar; la CITOFONÍA se desprograma y
 * se vuelve a programar desde Reprogramación, y la tarjeta muestra cuántos
 * celulares de la casa esperan ahí.
 *
 * RBAC: la comisión y el tesorero LEEN (ambos tabs); solo el tesorero OPERA
 * las transiciones (botones).
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { ApiError } from '../../lib/api/client';
import { vistaActualPorPath } from '../../lib/layout/vista';
import { listarLotes } from '../../lib/api/lotes';
import {
  confirmarReactivacion,
  confirmarSuspension,
  listarCandidatosSuspension,
  listarSuspensionesVigentes,
  reactivarCasa,
  suspenderCasa,
} from '../../lib/api/suspensiones-lote';
import type {
  CandidatoSuspensionRead,
  LoteResumen,
  SuspensionVigenteRead,
} from '../../lib/api/types';
import { useAuth } from '../../lib/auth/AuthContext';
import { ROLES_SUSPENDEN, tieneAlgunRol } from '../../lib/auth/roles';
import { formatCLP } from '../../lib/format';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';

type Tab = 'candidatos' | 'vigentes';

// ----- Helpers de presentación (lenguaje usuario-facing, convención #13) ----

type BadgeVariant = 'pending' | 'success' | 'warning' | 'danger' | 'info';

const ESTADO_LABEL: Record<string, { texto: string; variant: BadgeVariant }> = {
  en_proceso_suspension: { texto: 'En proceso de corte', variant: 'warning' },
  suspendido: { texto: 'Suspendida', variant: 'danger' },
  en_proceso_reactivacion: { texto: 'En proceso de reconexión', variant: 'info' },
  activo: { texto: 'Activa', variant: 'success' },
};

const NIVEL_LABEL: Record<string, { texto: string; variant: BadgeVariant }> = {
  suspendible: { texto: 'Suspendible', variant: 'danger' },
  moroso: { texto: 'Morosa', variant: 'warning' },
};

// Exportados para reuso en el preview dev-only (pages/dev/SuspensionesPreview).
export function ChipEstado({ estado }: { estado: string }) {
  const e = ESTADO_LABEL[estado] ?? { texto: estado, variant: 'pending' as BadgeVariant };
  return <Badge variant={e.variant}>{e.texto}</Badge>;
}

export function ChipNivel({ nivel }: { nivel: string }) {
  const n = NIVEL_LABEL[nivel] ?? { texto: nivel, variant: 'pending' as BadgeVariant };
  return <Badge variant={n.variant}>{n.texto}</Badge>;
}

/** Cuántos celulares de citofonía de la casa esperan en Reprogramación:
 *  por desprogramar (casa que se suspende) o por programar (que se reactiva).
 *  Exportado para reuso en el preview dev-only. */
export function textoCitofonia(estado: string, pendientes: number): string {
  if (pendientes === 0) return 'Citofonía: nada pendiente en Reprogramación.';
  const celulares = pendientes === 1 ? '1 celular' : `${pendientes} celulares`;
  return estado === 'en_proceso_reactivacion'
    ? `Citofonía: ${celulares} por volver a programar en Reprogramación.`
    : `Citofonía: ${celulares} por desprogramar en Reprogramación.`;
}

/** Banner de la doctrina espejo del HW (reutilizado en los modales). */
function BannerHW({ texto }: { texto: string }) {
  return (
    <div className="bg-soft-warning/40 border-l-4 border-state-warning p-3 rounded text-xs text-text-on-warning">
      {texto}
    </div>
  );
}

function ErrorBox({ texto }: { texto: string }) {
  return (
    <p className="text-sm text-state-danger bg-state-danger/10 border-l-4 border-state-danger p-2 rounded">
      {texto}
    </p>
  );
}

function mensajeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const detail =
      typeof err.body === 'object' && err.body && 'detail' in err.body
        ? (err.body as { detail: string }).detail
        : fallback;
    return `Error ${err.status}: ${detail}`;
  }
  return fallback;
}

// ----- Modal: suspender una casa (motivo + preview de accesos) -----

interface ModalSuspenderProps {
  loteId: string;
  identificador: string;
  onClose: () => void;
  onListo: () => void;
}

function ModalSuspender({ loteId, identificador, onClose, onListo }: ModalSuspenderProps) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!motivo.trim()) {
      setError('El motivo es obligatorio.');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await suspenderCasa(loteId, { motivo: motivo.trim() });
      onListo();
    } catch (err) {
      setError(mensajeError(err, 'No pudimos suspender la casa.'));
      setEnviando(false);
    }
  }

  return (
    <Modal
      title={`Suspender casa ${identificador}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={enviando}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleSubmit} disabled={enviando}>
            {enviando ? 'Registrando…' : 'Suspender'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Motivo de la suspensión
          </label>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="w-full rounded-md border-gray-300 text-sm"
            rows={3}
            required
            disabled={enviando}
            placeholder="Ej: morosidad ratificada por la Comisión"
          />
        </div>
        <BannerHW texto="Al guardar, la casa queda en proceso de corte y sus celulares de citofonía aparecen en Reprogramación para desprogramarlos de la central. El WiFi lo corta el sistema en eWeLink cuando confirmes el corte en la pestaña Suspendidas." />
        {error && <ErrorBox texto={error} />}
      </form>
    </Modal>
  );
}

// ----- Modal: reactivar (motivo + aviso morosidad no bloqueante) -----

interface ModalReactivarProps {
  item: SuspensionVigenteRead;
  onClose: () => void;
  onListo: () => void;
}

function ModalReactivar({ item, onClose, onListo }: ModalReactivarProps) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!motivo.trim()) {
      setError('El motivo es obligatorio.');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await reactivarCasa(item.suspension.id, { motivo: motivo.trim() });
      onListo();
    } catch (err) {
      setError(mensajeError(err, 'No pudimos reactivar la casa.'));
      setEnviando(false);
    }
  }

  return (
    <Modal
      title={`Reactivar casa ${item.identificador}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={enviando}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={enviando}>
            {enviando ? 'Registrando…' : 'Reactivar'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {!item.al_dia && (
          <div className="bg-soft-warning/40 border-l-4 border-state-warning p-3 rounded text-xs text-text-on-warning">
            <strong>Atención:</strong> esta casa registra una deuda de{' '}
            {formatCLP(item.monto_adeudado)}. Puedes reactivar igualmente; este
            es solo un aviso, no un bloqueo.
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Motivo de la reactivación
          </label>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="w-full rounded-md border-gray-300 text-sm"
            rows={3}
            required
            disabled={enviando}
            placeholder="Ej: regularizó la deuda con pago del 12-jun-2026"
          />
        </div>
        {item.citofonias_pendientes > 0 && (
          <BannerHW
            texto={`${textoCitofonia(item.suspension.estado, item.citofonias_pendientes)} Si ya los quitaste de la central, márcalos antes de reactivar: si no, el sistema los seguirá dando por programados y no aparecerán para volver a programarlos.`}
          />
        )}
        <BannerHW texto="Al guardar, la casa pasa a en proceso de reconexión y sus celulares de citofonía aparecen en Reprogramación para volver a programarlos. El WiFi lo restaura el sistema cuando confirmes la reconexión." />
        {error && <ErrorBox texto={error} />}
      </form>
    </Modal>
  );
}

// ----- Modal: confirmar transición de hardware (corte / reconexión) -----

interface ModalConfirmarProps {
  item: SuspensionVigenteRead;
  tipo: 'corte' | 'reconexion';
  onClose: () => void;
  onListo: () => void;
}

function ModalConfirmar({ item, tipo, onClose, onListo }: ModalConfirmarProps) {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const esCorte = tipo === 'corte';
  const titulo = esCorte
    ? `Confirmar corte · casa ${item.identificador}`
    : `Confirmar reconexión · casa ${item.identificador}`;

  async function handleConfirmar() {
    setEnviando(true);
    setError(null);
    try {
      if (esCorte) {
        await confirmarSuspension(item.suspension.id);
      } else {
        await confirmarReactivacion(item.suspension.id);
      }
      onListo();
    } catch (err) {
      setError(mensajeError(err, 'No pudimos registrar la confirmación.'));
      setEnviando(false);
    }
  }

  return (
    <Modal
      title={titulo}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={enviando}>
            Cancelar
          </Button>
          <Button
            variant={esCorte ? 'danger' : 'primary'}
            onClick={handleConfirmar}
            disabled={enviando}
          >
            {enviando ? 'Registrando…' : esCorte ? 'Confirmé el corte' : 'Confirmé la reconexión'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-text-primary">
          {esCorte
            ? 'Al confirmar, el sistema da de baja en eWeLink el WiFi de todos los residentes de la casa.'
            : 'Al confirmar, el sistema devuelve en eWeLink el WiFi que cortó la suspensión.'}
        </p>
        {item.citofonias_pendientes > 0 && (
          <BannerHW
            texto={`${textoCitofonia(item.suspension.estado, item.citofonias_pendientes)} Puedes confirmar igual: la citofonía se ${esCorte ? 'desprograma' : 'programa'} aparte.`}
          />
        )}
        {error && <ErrorBox texto={error} />}
      </div>
    </Modal>
  );
}

// ----- Página -----

type Accion =
  | { tipo: 'suspender'; loteId: string; identificador: string }
  | { tipo: 'reactivar'; item: SuspensionVigenteRead }
  | { tipo: 'confirmar'; item: SuspensionVigenteRead; corte: boolean }
  | null;

export function Suspensiones() {
  const { me } = useAuth();
  const { pathname } = useLocation();
  // Gate por CONTEXTO de ruta (Sprint 19a): la pantalla se monta en dos rutas
  // (hogar Tesorería `/tesoreria/suspensiones` + lectura Comisión
  // `/admin/suspensiones`). Operar solo desde la vista Tesorería; la vista
  // Comisión es SIEMPRE read-only, aunque el usuario sea tesorero — así "vista
  // Comisión = lectura" es fiel y transparente. El rol sigue exigiéndose
  // (defensa en profundidad; el backend revalida por rol).
  const esVistaTesoreria = vistaActualPorPath(pathname) === 'tesoreria';
  const puedeOperar =
    !!me && esVistaTesoreria && tieneAlgunRol(me.roles_vigentes, ROLES_SUSPENDEN);

  const [tab, setTab] = useState<Tab>('candidatos');
  const [candidatos, setCandidatos] = useState<CandidatoSuspensionRead[]>([]);
  const [vigentes, setVigentes] = useState<SuspensionVigenteRead[]>([]);
  const [lotes, setLotes] = useState<LoteResumen[]>([]);
  const [loteBuscado, setLoteBuscado] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accion, setAccion] = useState<Accion>(null);
  const [toast, setToast] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cand, vig, lts] = await Promise.all([
        listarCandidatosSuspension(),
        listarSuspensionesVigentes(),
        listarLotes(true),
      ]);
      setCandidatos(cand);
      setVigentes(vig);
      setLotes(lts);
    } catch {
      setError('No pudimos cargar las suspensiones. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  function onListo(mensaje: string) {
    setAccion(null);
    setToast(mensaje);
    setLoteBuscado('');
    cargar();
  }

  if (!me) return null;

  // Casas ya con suspensión vigente (no aparecen como suspendibles de nuevo).
  const lotesVigentes = new Set(vigentes.map((v) => v.suspension.lote_id));
  const loteSel = lotes.find((l) => l.id === loteBuscado);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-text-primary">Suspensiones</h2>
        <p className="text-sm text-text-secondary mt-0.5">
          Suspensión por morosidad de citofonía y wifi, por casa.
        </p>
        {!esVistaTesoreria && (
          <p className="text-xs text-text-secondary mt-1 inline-flex items-center gap-1">
            <Badge variant="info">Solo lectura</Badge>
            Las operaciones (suspender, reactivar, confirmar) se realizan desde
            la vista Tesorería.
          </p>
        )}
      </div>

      <div className="bg-soft-warning/40 border-l-4 border-state-warning p-4 rounded text-sm text-text-on-warning">
        <p className="font-medium">Qué hace el sistema y qué haces tú</p>
        <p className="text-xs mt-1">
          <strong>WiFi:</strong> lo corta el sistema en eWeLink al confirmar el
          corte, para todos los residentes de la casa, y devuelve esos mismos
          accesos al confirmar la reconexión. <strong>Citofonía:</strong> la
          desprogramas y la vuelves a programar tú en la central; los celulares
          de la casa aparecen en Reprogramación.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-line flex gap-4">
        <button
          type="button"
          onClick={() => setTab('candidatos')}
          className={`pb-2 text-sm font-medium border-b-2 -mb-px ${
            tab === 'candidatos'
              ? 'border-orange text-text-primary'
              : 'border-transparent text-text-secondary hover:text-text-primary'
          }`}
        >
          Suspendibles ({candidatos.length})
        </button>
        <button
          type="button"
          onClick={() => setTab('vigentes')}
          className={`pb-2 text-sm font-medium border-b-2 -mb-px ${
            tab === 'vigentes'
              ? 'border-orange text-text-primary'
              : 'border-transparent text-text-secondary hover:text-text-primary'
          }`}
        >
          Suspendidas / en proceso ({vigentes.length})
        </button>
      </div>

      {loading && (
        <div className="bg-bg-elevated rounded-lg shadow p-5 text-sm text-text-secondary">
          Cargando…
        </div>
      )}
      {error && !loading && (
        <div className="bg-state-danger/10 border-l-4 border-state-danger p-4 rounded text-sm text-text-primary">
          {error}
        </div>
      )}

      {/* Tab 1: candidatos */}
      {!loading && !error && tab === 'candidatos' && (
        <>
          {puedeOperar && (
            <div className="bg-bg-elevated rounded-lg shadow p-5">
              <label className="block text-sm font-medium text-text-primary mb-2">
                Suspender cualquier casa
              </label>
              <p className="text-xs text-text-secondary mb-2">
                No es necesario que esté en la lista de suspendibles.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={loteBuscado}
                  onChange={(e) => setLoteBuscado(e.target.value)}
                  className="rounded-md border-gray-300 text-sm w-full md:w-72"
                >
                  <option value="">Elige una casa…</option>
                  {lotes
                    .filter((l) => !lotesVigentes.has(l.id))
                    .map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.identificador} — {l.direccion}
                      </option>
                    ))}
                </select>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={!loteSel}
                  onClick={() =>
                    loteSel &&
                    setAccion({
                      tipo: 'suspender',
                      loteId: loteSel.id,
                      identificador: loteSel.identificador,
                    })
                  }
                >
                  Suspender
                </Button>
              </div>
            </div>
          )}

          <section>
            <h3 className="text-lg font-semibold text-text-primary mb-2">
              Suspendibles ({candidatos.length})
            </h3>
            <p className="text-xs text-text-secondary mb-3">
              Casas habilitadas para suspender según el régimen de morosidad
              vigente (plazo y meses de atraso se configuran en Superadmin).
              Suspender sigue siendo tu decisión. Listado calculado en vivo:
              una casa que se puso al día hoy ya no aparece.
            </p>
            {candidatos.length === 0 ? (
              <div className="bg-bg-elevated rounded-lg shadow p-5 text-sm text-text-secondary">
                No hay casas suspendibles.
              </div>
            ) : (
              <div className="bg-bg-elevated rounded-lg shadow overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-bg-page/40 text-left text-xs text-text-secondary uppercase">
                    <tr>
                      <th className="px-4 py-2 font-medium">Casa</th>
                      <th className="px-4 py-2 font-medium hidden md:table-cell">
                        Responsable
                      </th>
                      <th className="px-4 py-2 font-medium">Estado</th>
                      <th className="px-4 py-2 font-medium text-right">Deuda</th>
                      {puedeOperar && <th className="px-4 py-2" />}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {candidatos.map((c) => (
                      <tr key={c.lote_id}>
                        <td className="px-4 py-2 font-medium text-text-primary">
                          {c.identificador}
                          {/* Mobile: el responsable va bajo el número de casa. */}
                          <div className="md:hidden text-xs font-normal text-text-secondary">
                            {c.responsable ?? 'Sin responsable'}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-text-primary hidden md:table-cell">
                          {c.responsable ?? (
                            <span className="text-text-secondary">Sin responsable</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <ChipNivel nivel={c.nivel} />
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {formatCLP(c.monto_adeudado)}
                        </td>
                        {puedeOperar && (
                          <td className="px-4 py-2 text-right">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() =>
                                setAccion({
                                  tipo: 'suspender',
                                  loteId: c.lote_id,
                                  identificador: c.identificador,
                                })
                              }
                            >
                              Suspender
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {/* Tab 2: vigentes */}
      {!loading && !error && tab === 'vigentes' && (
        <section className="space-y-3">
          {vigentes.length === 0 ? (
            <div className="bg-bg-elevated rounded-lg shadow p-5 text-sm text-text-secondary">
              No hay casas suspendidas ni en proceso.
            </div>
          ) : (
            vigentes.map((v) => (
              <article key={v.suspension.id} className="bg-bg-elevated rounded-lg shadow p-4">
                <header className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-text-primary">
                      Casa {v.identificador}
                    </span>
                    <ChipEstado estado={v.suspension.estado} />
                  </div>
                  {puedeOperar && (
                    <div className="flex gap-2">
                      {v.suspension.estado === 'en_proceso_suspension' && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => setAccion({ tipo: 'confirmar', item: v, corte: true })}
                        >
                          Confirmé el corte
                        </Button>
                      )}
                      {v.suspension.estado === 'suspendido' && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setAccion({ tipo: 'reactivar', item: v })}
                        >
                          Reactivar
                        </Button>
                      )}
                      {v.suspension.estado === 'en_proceso_reactivacion' && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setAccion({ tipo: 'confirmar', item: v, corte: false })}
                        >
                          Confirmé la reconexión
                        </Button>
                      )}
                    </div>
                  )}
                </header>

                <p className="text-sm text-text-primary bg-bg-page/40 p-2 rounded mb-3">
                  <span className="font-medium">Motivo:</span>{' '}
                  {v.suspension.motivo_suspension}
                </p>

                {!v.al_dia && (
                  <p className="text-xs text-text-on-warning bg-soft-warning/40 border-l-4 border-state-warning p-2 rounded mb-3">
                    Deuda vigente: {formatCLP(v.monto_adeudado)}
                  </p>
                )}

                <p className="text-sm text-text-secondary">
                  {textoCitofonia(v.suspension.estado, v.citofonias_pendientes)}
                </p>
              </article>
            ))
          )}
        </section>
      )}

      {/* Modales */}
      {accion?.tipo === 'suspender' && (
        <ModalSuspender
          loteId={accion.loteId}
          identificador={accion.identificador}
          onClose={() => setAccion(null)}
          onListo={() => onListo('Casa marcada en proceso de corte.')}
        />
      )}
      {accion?.tipo === 'reactivar' && (
        <ModalReactivar
          item={accion.item}
          onClose={() => setAccion(null)}
          onListo={() => onListo('Casa marcada en proceso de reconexión.')}
        />
      )}
      {accion?.tipo === 'confirmar' && (
        <ModalConfirmar
          item={accion.item}
          tipo={accion.corte ? 'corte' : 'reconexion'}
          onClose={() => setAccion(null)}
          onListo={() =>
            onListo(
              accion.corte
                ? 'Corte confirmado. Casa suspendida.'
                : 'Reconexión confirmada. Casa activa.',
            )
          }
        />
      )}

      {toast && (
        <div className="fixed bottom-4 right-4 bg-bg-elevated text-text-primary border border-line px-4 py-2 rounded shadow-lg text-sm z-50">
          {toast}
        </div>
      )}
    </div>
  );
}
